import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { createRoot } from 'react-dom/client';
import { useState } from 'react';

// Composant pour une page PDF
export const VoucherPdfPage = ({ sections, renderSection, config }) => {
    return (
        <div
            style={{
                width: '794px',          // A4 en pixels
                minHeight: '1123px',
                padding: config.padding || 40,
                background: config.backgroundColor || '#fff',
                fontFamily: config.fontFamily || "'Inter', sans-serif",
                fontSize: config.fontSize || 16,
                boxSizing: 'border-box',
                color: '#1f2937'
            }}
        >
            {sections.map(section => (
                <div
                    key={section.id}
                    style={{
                        pageBreakInside: 'avoid',
                        marginBottom: config.gap || 16
                    }}
                >
                    {renderSection(section)}
                </div>
            ))}
        </div>
    );
};

// Pagination des sections
export const paginateSectionsByCanvas = async ({
    sections,
    renderSection,
    config
}) => {
    const pages = [];
    let currentPage = [];
    let currentHeight = 0;

    // dimensions A4 en px (approx avec scale 2)
    const PAGE_HEIGHT_PX = 1123 * 2;
    const PAGE_PADDING_PX = 80 * 2;
    const MAX_HEIGHT = PAGE_HEIGHT_PX - PAGE_PADDING_PX;

    // conteneur OFFSCREEN
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.width = '794px';
    container.style.background = '#fff';
    document.body.appendChild(container);

    for (const section of sections) {
        const sectionWrapper = document.createElement('div');
        sectionWrapper.className = 'pdf-section';
        container.appendChild(sectionWrapper);

        const root = createRoot(sectionWrapper);
        root.render(renderSection(section));

        await new Promise(r => setTimeout(r, 100));

        // 🔥 MESURE RÉELLE
        const canvas = await html2canvas(sectionWrapper, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff'
        });

        const sectionHeight = canvas.height;

        root.unmount();
        container.removeChild(sectionWrapper);

        // PAGINATION RÉELLE
        if (currentHeight + sectionHeight > MAX_HEIGHT) {
            pages.push([...currentPage]);
            currentPage = [];
            currentHeight = 0;
        }

        currentPage.push(section);
        currentHeight += sectionHeight;
    }

    if (currentPage.length) {
        pages.push([...currentPage]);
    }

    document.body.removeChild(container);
    return pages;
};


// Dessiner le filigrane
export const drawWatermark = (pdf, cfg) => {
    if (!cfg?.showWatermark) return;

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    try {
        pdf.saveGraphicsState();
        pdf.setGState(new pdf.GState({
            opacity: (cfg.watermarkOpacity || 15) / 100
        }));

        // Calcul de la taille du filigrane
        const getWatermarkSize = () => {
            const size = cfg.watermarkSize || 'large';
            const sizeMap = {
                'small': { text: 64, image: 70 },      // En CAS DE PB change ceciiii    
                'medium': { text: 96, image: 150 }, // En CAS DE PB change ceciiii
                'large': { text: 128, image: 200 }, // En CAS DE PB change ceciiii
                'xlarge': { text: 160, image: 300 } // En CAS DE PB change ceciiii
            };
            return sizeMap[size] || sizeMap.large;
        };

        const watermarkSize = getWatermarkSize();
        const rotation = cfg.watermarkRotation || -45;

        if (cfg.watermarkType === 'text' && cfg.watermarkText) {
            // Filigrane texte
            pdf.setFontSize(watermarkSize.text);
            pdf.setTextColor(128, 128, 128); // Gris

            if (cfg.watermarkPosition === 'repeat') {
                // Filigrane texte répété
                const cols = Math.ceil(pageWidth / 300);
                const rows = Math.ceil(pageHeight / 200);
                const colSpacing = pageWidth / cols;
                const rowSpacing = pageHeight / rows;

                for (let col = 0; col < cols; col++) {
                    for (let row = 0; row < rows; row++) {
                        const x = (col + 0.5) * colSpacing;
                        const y = (row + 0.5) * rowSpacing;
                        
                        pdf.text(cfg.watermarkText, x, y, {
                            align: 'center',
                            angle: rotation
                        });
                    }
                }
            } else {
                // Filigrane texte unique selon la position
                let x, y;

                switch (cfg.watermarkPosition) {
                    case 'center':
                        x = pageWidth / 2;
                        y = pageHeight / 2;
                        break;
                    case 'diagonal':
                        x = pageWidth / 3;
                        y = pageHeight / 3;
                        break;
                    case 'corner':
                        x = pageWidth - 100;
                        y = pageHeight - 50;
                        break;
                    default:
                        x = pageWidth / 2;
                        y = pageHeight / 2;
                }

                pdf.text(cfg.watermarkText, x, y, {
                    align: 'center',
                    angle: rotation
                });
            }
        } else if (cfg.watermarkType === 'image' && cfg.watermarkUrl) {
            // Filigrane image
            const imgSize = watermarkSize.image;

            if (cfg.watermarkPosition === 'repeat') {
                // Image répétée
                const cols = Math.ceil(pageWidth / 200);
                const rows = Math.ceil(pageHeight / 150);
                const colSpacing = pageWidth / cols;
                const rowSpacing = pageHeight / rows;

                for (let col = 0; col < cols; col++) {
                    for (let row = 0; row < rows; row++) {
                        const x = (col + 0.5) * colSpacing - imgSize / 2;
                        const y = (row + 0.5) * rowSpacing - imgSize / 2;
                        
                        pdf.addImage({
                            imageData: cfg.watermarkUrl,
                            x: x,
                            y: y,
                            width: imgSize,
                            height: imgSize,
                            rotation: rotation
                        });
                    }
                }
            } else {
                // Image unique selon la position
                let x, y;

                switch (cfg.watermarkPosition) {
                    case 'center':
                        x = pageWidth / 2 - imgSize / 2;
                        y = pageHeight / 2 - imgSize / 2;
                        break;
                    case 'diagonal':
                        x = pageWidth / 4 - imgSize / 2;
                        y = pageHeight / 3 - imgSize / 2;
                        break;
                    case 'corner':
                        x = pageWidth - imgSize - 40;
                        y = pageHeight - imgSize - 40;
                        break;
                    default:
                        x = pageWidth / 2 - imgSize / 2;
                        y = pageHeight / 2 - imgSize / 2;
                }

                // Ajouter l'image en tant que filigrane
                pdf.addImage({
                    imageData: cfg.watermarkUrl,
                    x: x/2,
                    y: y/2,
                    width: imgSize,
                    height: imgSize,
                    rotation: rotation
                });
            }
        }

        pdf.restoreGraphicsState();
    } catch (error) {
        console.error('Erreur lors du dessin du filigrane:', error);
        
        // En cas d'erreur, essayer de dessiner un filigrane texte de secours
        try {
            pdf.saveGraphicsState();
            pdf.setGState(new pdf.GState({ opacity: 0.1 }));
            pdf.setFontSize(60);
            pdf.setTextColor(128, 128, 128);
            
            // Position par défaut au centre
            pdf.text(cfg.watermarkText || 'WATERMARK', 
                     pageWidth / 2, 
                     pageHeight / 2, 
                     { align: 'center', angle: -45 });
                     
            pdf.restoreGraphicsState();
        } catch (fallbackError) {
            console.error('Erreur même avec le filigrane de secours:', fallbackError);
        }
    }
};
// Dessiner le logo
export const drawLogo = (pdf, logo) => {
    if (!logo?.customization?.enabled || !logo.logoUrl) return;

    try {
        // Convertir l'URL en base64 si nécessaire
        const addImageToPDF = async () => {
            try {
                const imgData = await urlToBase64(logo.logoUrl);

                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();

                let x = 10;
                let y = 10;
                const size = logo.customization.size === 'small' ? 20 :
                    logo.customization.size === 'large' ? 40 : 30;

                // Position selon la configuration
                if (logo.customization.position.includes('right')) {
                    x = pageWidth - size - 10;
                } else if (logo.customization.position.includes('center')) {
                    x = (pageWidth - size) / 2;
                }

                if (logo.customization.position.includes('bottom')) {
                    y = pageHeight - size - 10;
                }

                pdf.addImage(imgData, 'PNG', x, y, size, size);
            } catch (error) {
                console.error('Erreur lors de l\'ajout du logo:', error);
            }
        };

        addImageToPDF();
    } catch (error) {
        console.error('Erreur dans drawLogo:', error);
    }
};

// Fonction utilitaire pour convertir URL en base64
const urlToBase64 = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = url;
    });
};

export const VoucherLogoOverlay = ({
  logoConfig,
  setLogoConfig,
  draggable = false
}) => {
  const [dragState, setDragState] = useState(null);

  const handleMouseDown = (e) => {
    if (!draggable) return;

    e.preventDefault();
    setDragState({
      startX: e.clientX,
      startY: e.clientY,
      logoX: logoConfig.customization.x,
      logoY: logoConfig.customization.y
    });
  };

  const handleMouseMove = (e) => {
    if (!dragState || !draggable) return;

    const dx = e.clientX - dragState.startX;
    const dy = e.clientY - dragState.startY;

    setLogoConfig(prev => ({
      ...prev,
      customization: {
        ...prev.customization,
        x: dragState.logoX + dx,
        y: dragState.logoY + dy
      }
    }));
  };

  const handleMouseUp = () => setDragState(null);

  if (!logoConfig?.customization?.enabled || !logoConfig.logoUrl) return null;

  return (
    <div
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{ position: 'absolute', inset: 0, pointerEvents: draggable ? 'auto' : 'none' }}
    >
      <div
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          left: logoConfig.customization.x,
          top: logoConfig.customization.y,
          cursor: draggable ? 'grab' : 'default',
          opacity: logoConfig.customization.opacity / 100,
          zIndex: 20
        }}
      >
        <img
          src={logoConfig.logoUrl}
          draggable={false}
          style={{
            width: logoConfig.customization.width,
            height: 'auto',
            pointerEvents: 'none'
          }}
        />
      </div>
    </div>
  );
};


// Fonction principale d'export PDF
export const exportToPDF = async ({
    sections,
    renderSection,
    config,
    watermarkConfig,
    logoConfig
}) => {
    try {
        const pages = await paginateSectionsByCanvas({
            sections: sections.filter(s => s.visible !== false),
            renderSection,
            config
        });
        if (pages.length === 0) {
            throw new Error('Aucune section à exporter');
        }

        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const margin = 5;
        const usableWidth = pageWidth - margin * 2;

        for (let i = 0; i < pages.length; i++) {
            if (i > 0) pdf.addPage();

            // CONTENEUR OFFSCREEN
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '-9999px';

            container.style.width = '794px'; // largeur A4 réelle (important)
            container.style.backgroundColor = config.backgroundColor || '#ffffff';

            document.body.appendChild(container);

            const rootEl = document.createElement('div');
            container.appendChild(rootEl);

            const root = createRoot(rootEl);
            root.render(
                <><VoucherPdfPage
                    sections={pages[i]}
                    renderSection={renderSection}
                    config={config} />
                    <VoucherLogoOverlay
                        logoConfig={logoConfig}
                        draggable={false} />
                        </>
                
            );

            await new Promise(r => setTimeout(r, 300));

            try {
                const canvas = await html2canvas(rootEl, {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: config.backgroundColor || '#ffffff'
                });

                const imgData = canvas.toDataURL('image/png');

                // 🔥 CALCUL COMME TA 2ᵉ FONCTION
                const imgHeight = (canvas.height * usableWidth) / canvas.width;

                pdf.addImage(
                    imgData,
                    'PNG',
                    margin,
                    margin,
                    usableWidth,
                    imgHeight
                );

                if (watermarkConfig) {
                    drawWatermark(pdf, watermarkConfig);
                }

                if (logoConfig) {
                    await drawLogo(pdf, logoConfig);
                }

            } finally {
                root.unmount();
                document.body.removeChild(container);
            }
        }

        const bookingNumber =
            sections.find(s => s.id === 'header')?.data?.bookingNumber || 'voucher';

        pdf.save(`voucher-${bookingNumber}-${new Date().toISOString().split('T')[0]}.pdf`);
        return true;

    } catch (error) {
        console.error('Erreur export PDF:', error);
        throw error;
    }
};
